import { useCallback, useMemo } from 'react';
import { defineMessages } from 'react-intl';

import type { IntlWithFormatter } from '../../../contexts/IntlContext';
import { useIntl } from '../../../hooks/useIntl';
import type { OrderFormSides } from '../../../types/OrderFormSides';
import type { Security } from '../../../types/Security';
import type { ButtonVariants } from '../../Button';
import { EllipsisText } from '../../Form/BaseSelect/styles';
import { Input } from '../../Form/Input';
import { SearchSelect } from '../../Form/SearchSelect';
import { FormControlSizes, type FormControlProps } from '../../Form/types';
import { QuickOptions } from '../../QuickOptions';
import { Text } from '../../Text';
import { QuickOptionsContainer } from '../styles';
import { useAmountInput } from '../useAmountInput';
import { CurrencyButtons } from './CurrencyButtons';
import type { CurrencyControlsProps } from './CurrencyControlsProps';
import type { QtyInputCurrencyOptions } from './getDefaultCurrencyOptions';

const messages = defineMessages({
  contract: {
    defaultMessage: 'Contract',
    id: 'Forms.QuantityInput.contract',
  },
  max: {
    defaultMessage: 'Max',
    id: 'Forms.QuantityInput.max',
  },
  numContractEqualsValueCurrency: {
    defaultMessage: '1 contract = {value} {currency}',
    id: 'Forms.QuantityInput.numContractEqualsValueCurrency',
  },
  valuePercent: {
    defaultMessage: '{value}%',
    id: 'Forms.QuantityInput.valuePercent',
  },
});

const generateQtyQuickOptions = (intl: IntlWithFormatter) => {
  return [
    {
      label: intl.formatMessage(messages.valuePercent, { value: '10' }),
      value: 0.1,
    },
    {
      label: intl.formatMessage(messages.valuePercent, { value: '25' }),
      value: 0.25,
    },
    {
      label: intl.formatMessage(messages.valuePercent, { value: '50' }),
      value: 0.5,
    },
    {
      label: intl.formatMessage(messages.valuePercent, { value: '75' }),
      value: 0.75,
    },
    {
      label: intl.formatMessage(messages.max),
      value: 1,
    },
  ];
};

export type QuantityInputProps<KeyType = string> = {
  side: OrderFormSides;
  security: Security | undefined;
  value: string;
  /** The selected currency */
  currency?: KeyType;
  /** The currencies the user can choose between. */
  currencyOptions: QtyInputCurrencyOptions<KeyType>[];
  /** The control to use to show the different currencies available for selection. Defaults to buttons. */
  currencyControl?: 'buttons' | 'selector';
  /** Overrides side-derived coloring for currency buttons when the action is not represented by side alone. */
  currencyButtonVariant?: ButtonVariants;
  /** Theme color for the currency suffix, such as `yellow.default`. */
  suffixColor?: string;
  /** A simple toggle to just show or not show the currency control. Defaults to true. */
  showCurrencyControl?: boolean;
  /**
   * Set to true to disable the user from selecting any currencies.
   * The currency control is also disabled if the main `disabled` prop is set to true.
   */
  disableCurrencyControl?: boolean;
  /** Maps a currency option value to the string key required by the button control. Defaults to identity for string currencies. */
  getCurrencyOptionKey?: (currency: KeyType) => string;
  onCurrencyChange: (currency: KeyType) => void;
  min?: string;
  max?: string;
  onChange: (value?: string) => void;
  onQuickOptionClicked?: (value: number) => void;
  showQuickOptions?: boolean;
  disableQuickOptions?: boolean;
  'data-testid'?: string;
} & Omit<FormControlProps<HTMLInputElement>, 'security' | 'onChange' | 'value'>;

function defaultGetCurrencyOptionKey<KeyType = string>(currency: KeyType): string {
  return String(currency);
}

export const QuantityInput = <KeyType,>({
  side,
  security,
  value,
  disabled,

  currency,
  currencyOptions,
  currencyControl = 'buttons',
  currencyButtonVariant,
  suffixColor,
  showCurrencyControl = true,
  disableCurrencyControl,
  getCurrencyOptionKey = defaultGetCurrencyOptionKey,

  showQuickOptions = false,
  disableQuickOptions,
  onChange,
  onCurrencyChange,
  onQuickOptionClicked,
  min,
  max,
  'data-testid': dataTestId,
  onFocus,
  ...props
}: QuantityInputProps<KeyType>) => {
  const intl = useIntl();
  if (showQuickOptions && onQuickOptionClicked == null) {
    throw new Error('Cannot show quick options without quick option handler');
  }

  const stringCurrency = typeof currency === 'string' ? currency : undefined;
  const inputProps = useAmountInput({
    currency: stringCurrency,
    asset: security,
    value,
    onChange,
    min,
    max,
    onFocus,
  });

  const handleChangeCurrency = useCallback(
    (currency: KeyType) => {
      onCurrencyChange(currency);
      inputProps.ref.current?.focus();
    },
    [onCurrencyChange, inputProps]
  );

  const selectedCurrencyOption = useMemo(
    () =>
      currency == null
        ? undefined
        : currencyOptions.find(co => getCurrencyOptionKey(co.currency) === getCurrencyOptionKey(currency)),
    [currencyOptions, currency, getCurrencyOptionKey]
  );

  // ToggleGroup currently requires string values, so map arbitrary currency values to caller-provided string keys.
  const buttonCurrencyLookup = useMemo(() => {
    return new Map(currencyOptions.map(co => [getCurrencyOptionKey(co.currency), co.currency]));
  }, [currencyOptions, getCurrencyOptionKey]);
  const buttonCurrencyOptions = useMemo(
    () =>
      currencyOptions.map(co => ({
        ...co,
        currency: getCurrencyOptionKey(co.currency),
      })),
    [currencyOptions, getCurrencyOptionKey]
  );
  const selectedButtonCurrency = useMemo(() => {
    if (currency == null) {
      return undefined;
    }
    const key = getCurrencyOptionKey(currency);
    return buttonCurrencyLookup.has(key) ? key : undefined;
  }, [buttonCurrencyLookup, currency, getCurrencyOptionKey]);
  const handleButtonCurrencyChange = useCallback(
    (buttonCurrency: string) => {
      const mappedCurrency = buttonCurrencyLookup.get(buttonCurrency);
      if (mappedCurrency != null) {
        handleChangeCurrency(mappedCurrency);
      }
    },
    [buttonCurrencyLookup, handleChangeCurrency]
  );

  return (
    <>
      <Input
        {...props}
        {...inputProps}
        data-currency={currency}
        data-allowed-currencies={currencyOptions.map(co => co.label).join(', ')}
        disabled={disabled}
        suffix={
          showCurrencyControl ? (
            <>
              {currencyControl === 'buttons' ? (
                <CurrencyButtons
                  data-testid={`${dataTestId ?? 'quantity-input'}-currency-buttons`}
                  disabled={disabled || disableCurrencyControl}
                  currency={selectedButtonCurrency}
                  side={side}
                  currencyButtonVariant={currencyButtonVariant}
                  suffixColor={suffixColor}
                  currencyOptions={buttonCurrencyOptions}
                  handleChangeCurrency={handleButtonCurrencyChange}
                />
              ) : (
                <CurrencySelect
                  data-testid={`${dataTestId ?? 'quantity-input'}-currency-select`}
                  disabled={disabled || disableCurrencyControl}
                  currency={currency}
                  side={side}
                  suffixColor={suffixColor}
                  currencyOptions={currencyOptions}
                  getCurrencyOptionKey={getCurrencyOptionKey}
                  handleChangeCurrency={handleChangeCurrency}
                />
              )}
            </>
          ) : (
            <Text color={suffixColor} pr="spacing4">
              {selectedCurrencyOption?.label}
            </Text>
          )
        }
        type="number"
        data-testid={dataTestId ?? 'quantity-input'}
      />
      {showQuickOptions && onQuickOptionClicked != null && (
        <QuickOptionsContainer>
          <QuickOptions
            options={generateQtyQuickOptions(intl)}
            onOptionClick={onQuickOptionClicked}
            disabled={disabled || disableQuickOptions}
          />
        </QuickOptionsContainer>
      )}
    </>
  );
};

function CurrencySelect<KeyType = string>({
  currencyOptions,
  currency,
  getCurrencyOptionKey,
  handleChangeCurrency,
  disabled,
  suffixColor,
  'data-testid': dataTestId,
}: CurrencyControlsProps<KeyType> & { getCurrencyOptionKey: (currency: KeyType) => string }) {
  const renderColoredLabel = useCallback(
    ({ item }: { item: QtyInputCurrencyOptions<KeyType> | undefined }) => (
      <EllipsisText color={suffixColor}>{item == null ? null : item.label}</EllipsisText>
    ),
    [suffixColor]
  );
  const selectedItem = useMemo(
    () =>
      currency == null
        ? undefined
        : currencyOptions.find(co => getCurrencyOptionKey(co.currency) === getCurrencyOptionKey(currency)),
    [currency, currencyOptions, getCurrencyOptionKey]
  );

  const handleChange = useCallback(
    (option?: QtyInputCurrencyOptions<KeyType>) => {
      if (option != null) {
        handleChangeCurrency(option.currency);
      }
    },
    [handleChangeCurrency]
  );

  const getLabel = useCallback((item: QtyInputCurrencyOptions<KeyType>) => item.label, []);
  const getDescription = useCallback((item: QtyInputCurrencyOptions<KeyType>) => item.description ?? '', []);

  const isItemDisabled = useCallback(() => {
    return disabled ?? false;
  }, [disabled]);

  return (
    <SearchSelect
      data-testid={dataTestId ?? 'quantity-input-currency-select'}
      selection={selectedItem}
      options={currencyOptions}
      getLabel={getLabel}
      disabled={disabled}
      getDescription={getDescription}
      isItemDisabled={isItemDisabled}
      dropdownWidth="320px"
      dropdownPlacement="bottom-start"
      dropdownSize={FormControlSizes.Default}
      onChange={handleChange}
      size={FormControlSizes.Small}
      customButtonLayout={suffixColor == null ? undefined : renderColoredLabel}
      showDropdownSearch={false}
      initialSortByLabel={false}
      portalize
    />
  );
}
